import { isWorkspacePreview } from './workspacePreview';
import { waitForTracking } from '../components/Tracking/VisitorTracking';
/* eslint-disable no-undef */
import {
  startOfWeek,
  startOfMonth,
  addYears,
  addMonths,
  addDays,
  eachDayOfInterval,
  startOfDay,
  endOfDay,
  eachMonthOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval
} from 'date-fns';
import { formatDateWithPattern } from '.';
import { toast } from 'react-toastify';

const { parseISO, format } = require('date-fns');

export const getDayRange = date => {
  const start = new Date(date.setHours(0, 0, 0, 0));
  const end = new Date(date.setHours(23, 59, 59, 999));
  return { start, end };
};

export const getWeekRange = date => {
  const start = new Date(date.setDate(date.getDate() - date.getDay()));
  const end = new Date(date.setDate(date.getDate() - date.getDay() + 6));
  return { start, end };
};

const generateDateFormat = (groupBy, date) => {
  let groupKey;
  if (groupBy === 'day') {
    groupKey = format(date, 'yyyy-MM-dd');
  } else if (groupBy === 'week') {
    const startOfWeekDate = startOfWeek(date, { weekStartsOn: 1 });
    groupKey = format(startOfWeekDate, 'yyyy-MM-dd');
  } else if (groupBy === 'month') {
    groupKey = format(date, 'yyyy-MM-01');
  } else if (groupBy === 'year') {
    groupKey = format(date, 'yyyy');
  } else if (groupBy === 'total') {
    groupKey = 'total';
  }

  return groupKey;
};

const fillMissingData = (dateRange, type) => {
  const start = startOfDay(new Date(dateRange.startDate));
  const end = endOfDay(new Date(dateRange.endDate));

  switch (type) {
    case 'day':
      return eachDayOfInterval({ start, end });
    case 'month':
      return eachMonthOfInterval({ start, end });
    case 'week':
      return eachWeekOfInterval({ start, end });
    case 'year':
      return eachYearOfInterval({ start, end });
    default:
      throw new Error(`Unknown type: ${type}`);
  }
};

export const aggregateDataTotal = (data, groupName) => {
  const result = {};
  data?.forEach(entry => {
    const findData = entry?.[groupName];

    if (!result[findData]) {
      result[findData] = 0;
    }
    result[findData]++;
  });

  return result;
};

export const aggregateData = (data, keys, groupBy, dateRange) => {
  let allDates = [];
  const result = {};

  if (groupBy !== 'total' && dateRange) {
    allDates = fillMissingData(dateRange, groupBy);
    const obj = Object.fromEntries(keys.map(item => [item, null]));
    allDates.forEach(date => {
      const groupKey = generateDateFormat(groupBy, date);
      result[groupKey] = { date: format(date, 'yyyy-MM-dd'), ...obj };
    });
  }

  data?.forEach(entry => {
    const dateTemp = entry?.timestamp;
    const dateTime = new Date(
      dateTemp.seconds * 1000 + dateTemp.nanoseconds / 1000000
    );

    const date = format(dateTime, 'yyyy-MM-dd');

    const groupKey = generateDateFormat(groupBy, parseISO(date));

    if (!result[groupKey]) {
      result[groupKey] = groupBy === 'total' ? {} : { date };
      keys.forEach(key => (result[groupKey][key] = 0));
    }

    keys.forEach(key => {
      if (key in entry) {
        result[groupKey][key] += entry[key];
      } else {
        result[groupKey][key]++;
      }

      /*result[groupKey][key] +=
        entry?.[key] ??
        (result[groupKey][key] === 0 ? 1 : result[groupKey][key]); */
    });
  });

  return result;
};

export const getLastSixMonthsRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 6);
  return { start, end };
};

const wordToNumber = word => {
  const words = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10
  };
  return words?.[word] || word;
};

const parseKeyword = keyword => {
  const parts = keyword.split('_');
  const timeFrame = parts[0];
  const amount = parts?.[1];
  const unit = parts?.[2];
  const extra = parts.slice(3).join('_');
  return {
    timeFrame,
    amount,
    unit,
    extra
  };
};

const modifyDate = (unit, getDate, direction) => {
  switch (unit) {
    case 'day':
    case 'days':
      return addDays(getDate, direction);
    case 'year':
    case 'years':
      return addYears(getDate, direction);
    case 'month':
    case 'months':
      return addMonths(getDate, direction);
    case 'week':
    case 'weeks':
      return addDays(getDate, direction * 7);
    default:
      return getDate;
  }
};

export const getDateFromMagicKeyword = keyword => {
  // exp: last_one_month, last_one_month_first_day
  if (!keyword) return keyword;

  const getDate = new Date();
  const { timeFrame, amount, unit, extra } = parseKeyword(keyword);

  const operations = {
    all_time: () => '2006-01-01',
    now: () => formatDateWithPattern(getDate, 'yyyy-MM-dd'),
    this_year_start: () => {
      const startOfYear = new Date(getDate.getFullYear(), 0, 1);
      return formatDateWithPattern(startOfYear, 'yyyy-MM-dd');
    }
  };

  if (keyword in operations) {
    return operations[keyword]();
  }

  const amountInt = wordToNumber(amount);

  if (amountInt && !Number.isNaN(amountInt)) {
    const isLast = timeFrame === 'last';
    const direction = isLast ? -amountInt : amountInt;

    const modifiedDate = modifyDate(unit, getDate, direction);
    const getFormattedDate = (date, pattern = 'yyyy-MM-dd') =>
      formatDateWithPattern(date, pattern);

    if (extra === 'first_day') {
      return getFormattedDate(startOfMonth(modifiedDate));
    }
    if (extra === 'last_day') {
      return getFormattedDate(
        new Date(getDate.getFullYear(), getDate.getMonth() + direction + 1, 0)
      );
    }
    if (extra === 'with_start_of_week') {
      return getFormattedDate(startOfWeek(modifiedDate, { weekStartsOn: 1 }));
    }
    if (extra === 'with_start_of_month') {
      return getFormattedDate(startOfMonth(modifiedDate));
    }
    if (extra === 'and_year') {
      return getFormattedDate(modifiedDate, 'yyyy-MM');
    }

    if (extra === 'with_month_range') {
      return getFormattedDate(startOfMonth(modifiedDate));
    }

    return getFormattedDate(modifiedDate);
  }

  return keyword;
};

export const sendRequest = async (url, method = 'GET', filterData = null) => {
  const token = localStorage.getItem('authToken');
  const upperMethod = method.toUpperCase();

  if (upperMethod === 'POST' && /signup|lead|register/i.test(url))
    await waitForTracking();
  const options = {
    credentials: isWorkspacePreview() ? 'omit' : 'include',
    method: upperMethod,
    headers: {
      'Content-Type': 'application/json'
    }
  };

  if (token) {
    options.headers.Authorization = `Bearer ${token}`;
  }

  if (
    (upperMethod === 'POST' ||
      upperMethod === 'PATCH' ||
      upperMethod === 'PUT' ||
      upperMethod === 'DELETE') &&
    filterData
  ) {
    options.body = JSON.stringify(filterData);
  }

  const response = await fetch(
    process.env.REACT_APP_BACKEND_URL + url,
    options
  );

  const parseResponseBody = async responseObject => {
    const responseText = await responseObject.text();
    const responseContentType =
      responseObject.headers.get('content-type') || '';

    if (!responseText) return null;

    if (responseContentType.includes('application/json')) {
      return JSON.parse(responseText);
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      return responseText;
    }
  };

  const responseData = await parseResponseBody(response);

  if (!response.ok) {
    const errorData =
      responseData && typeof responseData === 'object'
        ? responseData
        : { message: 'Unexpected non-JSON error response from backend.' };

    if (errorData.error === 'SESSION_MISSING') {
      localStorage.removeItem('authToken');

      if (window.location.pathname !== '/signIn') {
        window.location.href = '/signIn';
      }
    }
    if (errorData.error === 'USER_MISSING') {
      toast.error('Authentication failed.');
    }
    if (errorData.error === 'TOO_MANY_ATTEMPTS') {
      toast.error('Too many login attempts. Please try again later!');
    }

    // DATA_NOT_FOUND
    const error = new Error(`Error: ${errorData.message}`);
    // Attach errorData to error object so it can be accessed in catch blocks
    error.errorData = errorData;
    error.response = { data: errorData };
    throw error;
  }

  if (response.status === 204 || responseData === null) {
    return {};
  }

  if (typeof responseData === 'string') {
    throw new Error(
      'Unexpected non-JSON success response from backend. Endpoint might be returning HTML instead of JSON.'
    );
  }

  return responseData;
};
